/**
 * LayoutPlotSettings — the per-layout PLOTSETTINGS struct (PDF-1 foundation).
 *
 * AutoCAD stores plot settings inside each LAYOUT object (OBJECTS section).
 * The vector PDF exporter (`apps/web/src/app/print/pdf/`) consumes this typed
 * struct so it never has to know DXF group-code details when building a page.
 *
 * # Scope (PDF-1)
 *
 * This module ships the **struct + sane defaults only**. The full DXF parse
 * (populating phys w/h 44/45, margins 40-43, plot-area type 74, window rect
 * 48/49/140/141, scale 142/143, flags 70, plot-style 7 from the LAYOUT object)
 * is deferred to **PDF-6** per `PLAN-plot-pdf.md` §5. The existing
 * `extractPageSetup` / `Layout` schema only captures a subset (name/units/
 * rotation/offset/limits) and the parser `continue`s on unknown group codes,
 * so adding the full parse later is low-risk and additive.
 *
 * # DXF group-code map (Autodesk PLOTSETTINGS reference)
 *
 *   1   plot configuration / page-setup name
 *   2   plot configuration file name
 *   4   paper-size name ("ISO A4 (210.00 x 297.00 MM)", "A4", …)
 *   6   plot view name
 *   40  unprintable margin — left   (mm)
 *   41  unprintable margin — bottom (mm)
 *   42  unprintable margin — right  (mm)
 *   43  unprintable margin — top    (mm)
 *   44  plot paper width  (physical, mm)
 *   45  plot paper height (physical, mm)
 *   46  plot origin X offset (mm)
 *   47  plot origin Y offset (mm)
 *   48/49/140/141  plot window lower-left / upper-right (X/Y)
 *   70  plot layout flags (see PlotLayoutFlags)
 *   72  plot paper units (0=inches, 1=mm, 2=pixels)
 *   73  plot rotation (0=0°, 1=90°, 2=180°, 3=270°)
 *   74  plot-area type (0=Display,1=Extents,2=Limits,3=View,4=Window,5=Layout)
 *   75  standard scale type
 *   142 numerator of custom print scale (real-world units)
 *   143 denominator of custom print scale (drawing units)
 *   7   current plot-style sheet name (CTB / STB)
 */

import { z } from "zod";

/**
 * Plot-area type — DXF group code 74. Determines which portion of the
 * layout / model is plotted.
 */
export enum PlotAreaType {
  /** 0 — Display: the current on-screen view. */
  Display = 0,
  /** 1 — Extents: bounding box of all (plottable, visible) geometry. */
  Extents = 1,
  /** 2 — Limits: the layout/model LIMMIN..LIMMAX rectangle. */
  Limits = 2,
  /** 3 — View: a named saved view. */
  View = 3,
  /** 4 — Window: the saved window rectangle (codes 48/49/140/141). */
  Window = 4,
  /** 5 — Layout: the full printable area of the sheet (default for layouts). */
  Layout = 5,
}

/** Plot paper units — DXF group code 72. */
export enum PlotPaperUnits {
  Inches = 0,
  Millimeters = 1,
  Pixels = 2,
}

/**
 * Plot rotation — DXF group code 73. Counter-clockwise quarter turns of the
 * plotted image relative to the paper.
 */
export enum PlotRotation {
  None = 0,
  Quarter = 1, // 90°
  Half = 2, // 180°
  ThreeQuarter = 3, // 270°
}

/**
 * Plot layout flags — DXF group code 70 (bit field). Only the flags the
 * vector exporter consults in PDF-1 are documented; the rest are kept for
 * round-trip fidelity once PDF-6 wires the full parse.
 */
export enum PlotLayoutFlags {
  /** 1 — PlotViewportBorders. */
  PlotViewportBorders = 1,
  /** 2 — ShowPlotStyles. */
  ShowPlotStyles = 2,
  /** 4 — PlotCentered. */
  PlotCentered = 4,
  /** 8 — PlotHidden. */
  PlotHidden = 8,
  /** 16 — UseStandardScale. */
  UseStandardScale = 16,
  /** 32 — PlotPlotStyles. */
  PlotPlotStyles = 32,
  /** 64 — ScaleLineweights. */
  ScaleLineweights = 64,
  /** 128 — PrintLineweights. */
  PrintLineweights = 128,
  /** 512 — DrawViewportsFirst. */
  DrawViewportsFirst = 512,
  /** 1024 — ModelType. */
  ModelType = 1024,
}

/** Unprintable / printable margins in mm (DXF group codes 40/41/42/43). */
export interface PlotMargins {
  /** Group 40 — left unprintable margin (mm). */
  left: number;
  /** Group 41 — bottom unprintable margin (mm). */
  bottom: number;
  /** Group 42 — right unprintable margin (mm). */
  right: number;
  /** Group 43 — top unprintable margin (mm). */
  top: number;
}

/** Plot window rectangle in plot-area coordinates (codes 48/49/140/141). */
export interface PlotWindow {
  /** Group 48 — lower-left X. */
  lowerLeftX: number;
  /** Group 49 — lower-left Y. */
  lowerLeftY: number;
  /** Group 140 — upper-right X. */
  upperRightX: number;
  /** Group 141 — upper-right Y. */
  upperRightY: number;
}

/** Plot offset (codes 46/47) — content shift within the printable area (mm). */
export interface PlotOffset {
  /** Group 46 — X offset (mm). */
  x: number;
  /** Group 47 — Y offset (mm). */
  y: number;
}

/** Custom print-scale ratio (codes 142 numerator / 143 denominator). */
export interface PlotScale {
  /** Group 142 — numerator (paper / real-world units). */
  numerator: number;
  /** Group 143 — denominator (drawing units). */
  denominator: number;
}

/**
 * Full per-layout plot settings. PDF MediaBox is always the **physical**
 * paper (`paperWidthMm` × `paperHeightMm`); the printable area is paper minus
 * margins; the offset/center shift content inside the printable area without
 * changing the MediaBox.
 */
export interface LayoutPlotSettings {
  /** Group 1 — page-setup name. */
  pageSetupName: string;
  /** Group 4 — paper-size name. */
  paperName: string;
  /** Group 44 — physical paper width (mm). */
  paperWidthMm: number;
  /** Group 45 — physical paper height (mm). */
  paperHeightMm: number;
  /** Group 72 — plot paper units. */
  units: PlotPaperUnits;
  /** Group 40/41/42/43 — unprintable margins (mm). */
  margins: PlotMargins;
  /** Group 74 — plot-area type. */
  plotAreaType: PlotAreaType;
  /** Group 48/49/140/141 — plot window rectangle (used when type=Window). */
  window: PlotWindow;
  /** Group 142/143 — custom print scale (Layout area is always 1:1). */
  scale: PlotScale;
  /** Group 46/47 — plot offset (mm). */
  offset: PlotOffset;
  /** Group 73 — plot rotation. */
  rotation: PlotRotation;
  /** Group 7 — current plot-style sheet (CTB/STB) name; "" = none. */
  plotStyleName: string;
  /** Group 70 — plot layout flags (bit field). */
  flags: number;
}

/**
 * Zod schema mirror of {@link LayoutPlotSettings} (PDF-6.1). Embedded on the
 * model `Layout` (`layoutSchema.plotSettings`) so the per-layout PLOTSETTINGS
 * survives `drawingDocumentSchema.parse(...)` validation. Defaults match
 * {@link defaultPlotSettings} so a `.parse({})` yields the canonical A4 page.
 */
export const layoutPlotSettingsSchema = z.object({
  pageSetupName: z.string().default(""),
  paperName: z.string().default("A4"),
  paperWidthMm: z.number().default(210),
  paperHeightMm: z.number().default(297),
  units: z.nativeEnum(PlotPaperUnits).default(PlotPaperUnits.Millimeters),
  margins: z
    .object({
      left: z.number(),
      bottom: z.number(),
      right: z.number(),
      top: z.number(),
    })
    .default({ left: 7, bottom: 7, right: 7, top: 7 }),
  plotAreaType: z.nativeEnum(PlotAreaType).default(PlotAreaType.Layout),
  window: z
    .object({
      lowerLeftX: z.number(),
      lowerLeftY: z.number(),
      upperRightX: z.number(),
      upperRightY: z.number(),
    })
    .default({ lowerLeftX: 0, lowerLeftY: 0, upperRightX: 0, upperRightY: 0 }),
  scale: z
    .object({
      numerator: z.number(),
      denominator: z.number(),
    })
    .default({ numerator: 1, denominator: 1 }),
  offset: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .default({ x: 0, y: 0 }),
  rotation: z.nativeEnum(PlotRotation).default(PlotRotation.None),
  plotStyleName: z.string().default(""),
  flags: z.number().int().default(PlotLayoutFlags.PrintLineweights),
});

/**
 * A4 portrait paper dimensions in mm (ISO 216). Used by the default.
 */
const A4_PORTRAIT_WIDTH_MM = 210;
const A4_PORTRAIT_HEIGHT_MM = 297;

/**
 * Default unprintable margins for an A4 sheet on a typical plotter (mm).
 * AutoCAD's "none" / virtual PDF plotter uses a uniform ~7.5 mm border on
 * most devices; we use the common 7 mm sides + slightly larger gripper edge.
 * Kept symmetric here so the default page centres content predictably; the
 * real per-device margins arrive with the PDF-6 PLOTSETTINGS parse.
 */
export const DEFAULT_A4_MARGINS: PlotMargins = {
  left: 7,
  bottom: 7,
  right: 7,
  top: 7,
};

/**
 * Default plot settings: A4 portrait, full-printable Layout area, 1:1 scale,
 * no rotation, no offset, no plot-style sheet, plot lineweights on (flag 128).
 *
 * Used whenever a layout carries no explicit PLOTSETTINGS (the common case
 * until the PDF-6 parse lands) so the page can still be built.
 */
export function defaultPlotSettings(): LayoutPlotSettings {
  return {
    pageSetupName: "",
    paperName: "A4",
    paperWidthMm: A4_PORTRAIT_WIDTH_MM,
    paperHeightMm: A4_PORTRAIT_HEIGHT_MM,
    units: PlotPaperUnits.Millimeters,
    margins: { ...DEFAULT_A4_MARGINS },
    plotAreaType: PlotAreaType.Layout,
    window: {
      lowerLeftX: 0,
      lowerLeftY: 0,
      upperRightX: 0,
      upperRightY: 0,
    },
    scale: { numerator: 1, denominator: 1 },
    offset: { x: 0, y: 0 },
    rotation: PlotRotation.None,
    plotStyleName: "",
    // PrintLineweights (128): plot lineweights as authored.
    flags: PlotLayoutFlags.PrintLineweights,
  };
}

/**
 * Whether the "scale lineweights" flag (bit 64) is set. When true the
 * lineweight is multiplied by the plot scale; otherwise lineweights plot at
 * their authored mm size regardless of scale (the AutoCAD default).
 */
export function plotScalesLineweights(settings: LayoutPlotSettings): boolean {
  return (settings.flags & PlotLayoutFlags.ScaleLineweights) !== 0;
}

/**
 * Printable area (paper minus margins) in mm. Always non-negative.
 */
export function printableAreaMm(settings: LayoutPlotSettings): {
  width: number;
  height: number;
} {
  return {
    width: Math.max(
      settings.paperWidthMm - settings.margins.left - settings.margins.right,
      0,
    ),
    height: Math.max(
      settings.paperHeightMm - settings.margins.top - settings.margins.bottom,
      0,
    ),
  };
}
